import { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Download, FileText, X } from 'lucide-react';
import { supabase } from '../supabase.js';
import { UNITS, SUPPLIER_CATEGORIES } from '../constants.js';
import {
  Button,
  Callout,
  Card,
  DateRange,
  EmptyState,
  FilterSearch,
  FilterSpacer,
  PageHeader,
  SegmentedControl,
  StatusPill,
  resolveDateRange,
  type DateRangeValue,
} from '../ui/index.js';

// ─── Netlify function helper ──────────────────────────────────────────────────
// In production the dashboard is served from the same Netlify site so relative
// paths work. In local dev use `netlify dev` (port 8090) or set
// VITE_NETLIFY_BASE_URL=http://localhost:8090 in apps/dashboard/.env.
const NETLIFY_BASE = (import.meta.env.VITE_NETLIFY_BASE_URL as string) ?? '';
const netlifyFn = (name: string) => `${NETLIFY_BASE}/.netlify/functions/${name}`;

type CategoryFilterMode = 'include' | 'exclude';

function parseCategoryFilterFromUrl(): { categories: Set<string>; mode: CategoryFilterMode } {
  const params = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
  const legacy = params.get('category');
  const cats = params.get('categories');
  const mode: CategoryFilterMode = params.get('categoryMode') === 'exclude' ? 'exclude' : 'include';
  const categories = new Set<string>();
  if (cats) {
    for (const c of cats.split(',')) {
      if (c) categories.add(c);
    }
  } else if (legacy) {
    categories.add(legacy);
  }
  return { categories, mode };
}

/** Tax service rejects an issuedAt span longer than 6 months. */
const TAX_LIST_MAX_MONTHS = 6;

function isoDay(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseIsoDay(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
}

function addMonthsClamped(d: Date, months: number): Date {
  const day = d.getDate();
  const result = new Date(d.getFullYear(), d.getMonth() + months, 1);
  const last = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(day, last));
  return result;
}

/** Split [from, to] into windows the tax list API will accept. */
function taxDateWindows(from: string, to: string): { from: string; to: string }[] {
  if (!from || !to || from > to) return [{ from, to }];
  const windows: { from: string; to: string }[] = [];
  let cursor = parseIsoDay(from);
  const end = parseIsoDay(to);
  while (cursor.getTime() <= end.getTime()) {
    const windowEnd = addMonthsClamped(cursor, TAX_LIST_MAX_MONTHS);
    windowEnd.setDate(windowEnd.getDate() - 1);
    const sliceEnd = windowEnd.getTime() < end.getTime() ? windowEnd : end;
    windows.push({ from: isoDay(cursor), to: isoDay(sliceEnd) });
    const next = new Date(sliceEnd);
    next.setDate(next.getDate() + 1);
    cursor = next;
  }
  return windows;
}

function categoryFilterLabel(categories: Set<string>, mode: CategoryFilterMode): string {
  if (categories.size === 0) return 'All';
  const n = categories.size;
  return mode === 'exclude' ? `Exclude (${n})` : `Include (${n})`;
}

function profileLabel(p: {
  full_name: string | null;
  username: string | null;
  email: string | null;
}): string | null {
  return p.full_name?.trim() || p.username?.trim() || p.email?.trim() || null;
}

function resolvePersonName(
  storedName: string | null,
  userId: string | null,
  profileMap: Map<string, string>,
): string | null {
  if (userId && profileMap.has(userId)) return profileMap.get(userId)!;
  if (!storedName) return null;
  if (storedName.includes('@')) return null;
  return storedName;
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface TaxInvoice {
  id: string; // UUID from tax service
  serialNo: string;
  supplierTin: string;
  supplierName: string;
  status: string; // 'ISSUED' | 'APPROVED' (tax service status)
  type: string; // 'GOODS' | 'EXCISE' | 'LEASING' | 'SERVICES'
  issuedAt: string;
  totalValue: number;
  totalVatAmount: number;
  total: number;
}

interface TaxGoodsItem {
  /** Tax service doesn't return individual goods in invoice-list — loaded on demand. */
  name: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  total: number;
}

interface GoodsSplitLine {
  ingredientId: string | null;
  newIngredientName: string;
  useExisting: boolean;
  txQuantity: number;
  txQuantityStr: string;
}

interface GoodsRow {
  goodsName: string;
  quantity: number; // original qty from invoice (display only)
  txQuantity: number; // user-editable qty used for the transaction
  txQuantityStr: string; // raw string for the input field (allows empty while typing)
  unit: string;
  unitPrice: number;
  total: number;
  ingredientId: string | null;
  newIngredientName: string;
  newIngredientUnit: string;
  useExisting: boolean;
  skip: boolean; // true = don't track this item in inventory
  splitMode: boolean;
  splits: GoodsSplitLine[];
}

type PrevMapping =
  | { mode: 'single'; ingredient: IngredientOption; ratio: number }
  | { mode: 'split'; splits: { ingredient: IngredientOption; qtyPerSourceUnit: number }[] };

function blankSplitLine(): GoodsSplitLine {
  return {
    ingredientId: null,
    newIngredientName: '',
    useExisting: true,
    txQuantity: 0,
    txQuantityStr: '0',
  };
}

function mappingKey(supplierTin: string, goodsName: string) {
  return `${supplierTin}|${goodsName}`;
}

function splitQtySum(g: GoodsRow) {
  return g.splits.reduce((sum, s) => sum + s.txQuantity, 0);
}

function isGoodsRowValid(g: GoodsRow): boolean {
  if (g.skip) return true;
  if (g.splitMode) {
    const active = g.splits.filter((s) => s.txQuantity > 0);
    if (active.length === 0) return false;
    return active.every((s) =>
      s.useExisting ? !!s.ingredientId : !!s.newIngredientName.trim(),
    );
  }
  return g.useExisting ? !!g.ingredientId : !!g.newIngredientName.trim();
}

interface IngredientOption {
  id: string;
  name: string;
  unit: string;
  cost_price: number;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function InvoicesView({ clientId }: { clientId: string | null }) {
  const { t } = useTranslation();
  const taxStatusLabel = (status: string) => {
    if (status.toUpperCase() === 'ISSUED') return t('invoices.taxIssued');
    if (status.toUpperCase() === 'APPROVED') return t('invoices.taxApproved');
    return status;
  };
  const internalStatusLabel = (status: string) => {
    if (status === 'pending') return t('invoices.pending');
    if (status === 'approved') return t('invoices.approved');
    if (status === 'reverted') return t('invoices.reverted');
    return status;
  };

  // Session token from tax service (obtained via edge function)
  const [token, setToken] = useState<string | null>(null);
  const [tin, setTin] = useState<string>('');
  const [tokenLoading, setTokLoad] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);

  // Supplier category map: TIN → { id, category }
  const [suppliersByTin, setSuppliersByTin] = useState<
    Map<string, { id: string; category: string }>
  >(new Map());

  // Invoice list
  const [range, setRange] = useState<DateRangeValue>(() => {
    const params = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
    const from = params.get('from');
    const to = params.get('to');
    if (from || to) {
      return {
        preset: 'custom',
        from:
          from ??
          (() => {
            const d = new Date();
            d.setMonth(d.getMonth() - 1);
            return d.toISOString().slice(0, 10);
          })(),
        to: to ?? new Date().toISOString().slice(0, 10),
      };
    }
    return { preset: '30d' };
  });
  const { from: dateFrom = '', to: dateTo = new Date().toISOString().slice(0, 10) } =
    resolveDateRange(range);
  const [invoices, setInvoices] = useState<TaxInvoice[]>([]);
  const [approvedIds, setApprovedIds] = useState<Set<string>>(new Set());
  // Map tax_invoice_id → { status, approvedBy, revertedBy, approvedAt, approvalNote }
  const [invoiceMeta, setInvoiceMeta] = useState<
    Map<
      string,
      {
        status: 'approved' | 'reverted' | 'pending';
        approvedBy: string | null;
        revertedBy: string | null;
        approvedAt: string | null;
        approvalNote: string | null;
        dbId: string;
      }
    >
  >(new Map());
  const [listLoading, setListLoading] = useState(false);
  const [listLoadedCount, setListLoadedCount] = useState(0);
  const [listError, setListError] = useState<string | null>(null);

  // Review panel
  const [reviewing, setReviewing] = useState<TaxInvoice | null>(null);
  const [goods, setGoods] = useState<GoodsRow[]>([]);
  const [rawInvoice, setRawInvoice] = useState<Record<string, unknown> | null>(null);
  const [goodsLoading, setGoodsLoad] = useState(false);
  const [goodsError, setGoodsError] = useState<string | null>(null);
  const [ingredients, setIngredients] = useState<IngredientOption[]>([]);
  const [approving, setApproving] = useState(false);
  const [approveMsg, setApproveMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [approvalNote, setApprovalNote] = useState('');
  const [reverting, setReverting] = useState<string | null>(null); // tax_invoice_id being reverted
  const [revertMsg, setRevertMsg] = useState<{ taxId: string; text: string; ok: boolean } | null>(
    null,
  );
  const [confirmingRevert, setConfirmingRevert] = useState<string | null>(null); // taxId awaiting revert confirm

  // Accordion — expanded row + per-row goods cache
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Map tax_invoice_id → goods items (null = loading, [] = empty/loaded)
  const [rowGoods, setRowGoods] = useState<
    Map<
      string,
      { name: string; quantity: number; unit: string; unitPrice: number; total: number }[] | null
    >
  >(new Map());

  // Column filters — initialized from URL hash query params
  const [supplierTinInput, setSupplierTinInput] = useState(() => {
    const raw = new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('supplierTin') ?? '';
    return raw.replace(/\D/g, '');
  });
  const [supplierTin, setSupplierTin] = useState(supplierTinInput);
  const [filterSupplier, setFilterSupplier] = useState(
    () => new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('supplier') ?? '',
  );
  const [filterSerial, setFilterSerial] = useState(
    () => new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('serial') ?? '',
  );
  const pendingOpenId = useRef(
    new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('open'),
  );
  const [filterTotal, setFilterTotal] = useState(
    () => new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('total') ?? '',
  );
  const [filterTaxStatus, setFilterTaxStatus] = useState(
    () => new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('taxStatus') ?? '',
  );
  const [filterInternal, setFilterInternal] = useState(
    () => new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('internal') ?? '',
  );
  const [filterApprovedBy, setFilterApprovedBy] = useState(
    () => new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('approvedBy') ?? '',
  );
  const [filterCategories, setFilterCategories] = useState(
    () => parseCategoryFilterFromUrl().categories,
  );
  const [filterCategoryMode, setFilterCategoryMode] = useState<CategoryFilterMode>(
    () => parseCategoryFilterFromUrl().mode,
  );
  const [categoryFilterOpen, setCategoryFilterOpen] = useState(false);
  const categoryFilterRef = useRef<HTMLDivElement>(null);
  const [approverNames, setApproverNames] = useState<string[]>([]);

  // Previous supplier+goods_name mappings (for auto-suggest + qty conversion)
  const prevMappings = useRef<Map<string, PrevMapping>>(new Map());
  // Guard against React StrictMode double-invocation of the token useEffect
  const tokenFetchInFlight = useRef(false);
  // Set to true once at least one fetchInvoices() call has completed
  const hasFetched = useRef(false);
  // Session-level cache of raw goods items keyed by tax invoice ID.
  // Invoice goods are immutable once issued, so this never needs invalidation.
  const rawGoodsCache = useRef<Map<string, Record<string, unknown>[]>>(new Map());
  // Accumulator for the current date-range fetch (all pages).
  // `fetchGen` invalidates in-flight loops when the range/token changes mid-fetch.
  const paginationRef = useRef({
    offset: 1,
    seenIds: new Set<string>(),
    allItems: [] as Record<string, unknown>[],
    token: '',
    tin: '',
    from: '',
    to: '',
    fetchGen: 0,
  });

  // ─── Tax token — cached in localStorage, refreshed only when expired/missing ──
  // The Armenian Tax Service rejects new token requests while a previous token
  // is still valid, so we cache and reuse until it expires.
  const TAX_TOKEN_KEY = 'onit.tax_token.v1';

  function getCachedToken(): { token: string; tin: string } | null {
    try {
      const raw = localStorage.getItem(TAX_TOKEN_KEY);
      if (!raw) return null;
      const { token, tin, expiresAt } = JSON.parse(raw) as {
        token: string;
        tin: string;
        expiresAt: number;
      };
      if (Date.now() >= expiresAt) {
        localStorage.removeItem(TAX_TOKEN_KEY);
        return null;
      }
      return { token, tin };
    } catch {
      return null;
    }
  }

  function setCachedToken(token: string, tin: string) {
    // Decode JWT expiry; fall back to 8-hour TTL if not decodable.
    let expiresAt = Date.now() + 8 * 60 * 60 * 1000;
    try {
      const part = token.split('.')[1];
      if (part) {
        const payload = JSON.parse(atob(part)) as { exp?: number };
        if (payload.exp) expiresAt = payload.exp * 1000 - 60_000; // 1 min before actual expiry
      }
    } catch {
      /* keep fallback */
    }
    localStorage.setItem(TAX_TOKEN_KEY, JSON.stringify({ token, tin, expiresAt }));
  }

  function clearCachedToken() {
    localStorage.removeItem(TAX_TOKEN_KEY);
  }

  async function loadToken() {
    if (!supabase) return;

    // Use cached token if still valid — avoids re-requesting while previous is live.
    const cached = getCachedToken();
    if (cached) {
      setToken(cached.token);
      setTin(cached.tin);
      return;
    }

    // Prevent concurrent fetches (React StrictMode mounts effects twice in dev)
    if (tokenFetchInFlight.current) return;
    tokenFetchInFlight.current = true;

    setTokLoad(true);
    setTokenError(null);

    try {
      // Step 1 — get credentials + last known token from DB via Edge Function
      const { data, error } = await supabase.functions.invoke('get-tax-token-from-settings');
      if (error) {
        setTokenError(error.message);
        return;
      }
      const creds = data as {
        tin?: string;
        username?: string;
        password?: string;
        lastToken?: string | null;
        error?: string;
      };
      if (creds.error) {
        setTokenError(creds.error);
        return;
      }
      if (!creds.tin || !creds.username || !creds.password) {
        setTokenError(t('invoices.credentialsMissing'));
        return;
      }

      // Step 2 — exchange credentials for JWT via Netlify
      // Inner helper so we can call it twice (once after auto-logout on error 0032).
      async function fetchToken(): Promise<{ token: string } | { error: string }> {
        const res = await fetch(netlifyFn('get-tax-token'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tin: creds.tin,
            username: creds.username,
            password: creds.password,
          }),
        });
        const d = (await res.json()) as { success?: boolean; token?: string; error?: string };
        if (!res.ok || !d.success || !d.token) {
          return { error: d.error ?? `Tax service error (${res.status})` };
        }
        return { token: d.token };
      }

      let result = await fetchToken();

      // Error 0032 — previous session token still active on the tax service.
      // Use the last token from the DB (works cross-device), falling back to
      // the locally cached token. Then retry login once.
      if ('error' in result && result.error.includes('0032')) {
        const staleToken = creds.lastToken ?? getCachedToken()?.token ?? null;
        if (staleToken) {
          // Best-effort logout via Netlify — ignore errors.
          try {
            await fetch(netlifyFn('logout-tax-token'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token: staleToken }),
            });
          } catch {
            /* ignore */
          }
          clearCachedToken();
          // Brief pause to let the tax service register the logout.
          await new Promise<void>((r) => setTimeout(r, 1500));
          result = await fetchToken();
        } else {
          setTokenError(t('invoices.taxSessionActive'));
          return;
        }
      }

      if ('error' in result) {
        setTokenError(result.error);
        return;
      }

      // Step 3 — persist new token in DB so any device can recover from 0032 next time.
      void supabase.functions.invoke('save-tax-token', { body: { token: result.token } });

      setCachedToken(result.token, creds.tin);
      setToken(result.token);
      setTin(creds.tin);
    } catch (err) {
      setTokenError((err as Error).message);
    } finally {
      setTokLoad(false);
      tokenFetchInFlight.current = false;
    }
  }

  // Auto-load token on mount
  useEffect(() => {
    void loadToken();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fetch invoices when token first becomes available
  useEffect(() => {
    if (token) void fetchInvoices();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Commit a supplier TIN once it is empty or a full 8-digit ՀՎՀՀ.
  // Shorter values still apply on blur / Enter so a partial TIN can be searched.
  useEffect(() => {
    const digits = supplierTinInput.replace(/\D/g, '');
    if (digits.length !== 0 && digits.length < 8) return;
    if (digits === supplierTin) return;
    const id = window.setTimeout(() => setSupplierTin(digits), 400);
    return () => window.clearTimeout(id);
  }, [supplierTinInput, supplierTin]);

  // Re-fetch when date range or supplier TIN changes (token guard prevents premature calls)
  useEffect(() => {
    if (token) void fetchInvoices();
  }, [range, supplierTin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!categoryFilterOpen) return;
    function onPointerDown(e: MouseEvent) {
      if (categoryFilterRef.current?.contains(e.target as Node)) return;
      setCategoryFilterOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [categoryFilterOpen]);

  // Keep all filters in sync with URL hash
  useEffect(() => {
    const p = new URLSearchParams();
    if (dateFrom) p.set('from', dateFrom);
    if (dateTo) p.set('to', dateTo);
    if (supplierTin) p.set('supplierTin', supplierTin);
    if (filterSupplier) p.set('supplier', filterSupplier);
    if (filterSerial) p.set('serial', filterSerial);
    if (filterTotal) p.set('total', filterTotal);
    if (filterTaxStatus) p.set('taxStatus', filterTaxStatus);
    if (filterInternal) p.set('internal', filterInternal);
    if (filterApprovedBy) p.set('approvedBy', filterApprovedBy);
    if (filterCategories.size > 0) {
      p.set('categories', [...filterCategories].join(','));
      p.set('categoryMode', filterCategoryMode);
    }
    const newHash = `#/invoices?${p.toString()}`;
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', newHash);
    }
  }, [
    range,
    supplierTin,
    filterSupplier,
    filterSerial,
    filterTotal,
    filterTaxStatus,
    filterInternal,
    filterApprovedBy,
    filterCategories,
    filterCategoryMode,
  ]);

  // ─── Load previously-approved invoice IDs from DB ─────────────────────────

  async function loadSuppliers() {
    if (!supabase || !clientId) return;
    const { data } = await supabase
      .from('suppliers')
      .select('id, tin, category')
      .eq('client_id', clientId);
    if (data) {
      setSuppliersByTin(
        new Map(data.map((s) => [s.tin, { id: s.id, category: s.category ?? '' }])),
      );
    }
  }

  async function updateSupplierCategory(tin: string, category: string, supplierName?: string) {
    if (!supabase || !clientId || !tin) return;
    const supplier = suppliersByTin.get(tin);
    if (supplier) {
      // Optimistic update
      setSuppliersByTin((prev) => {
        const next = new Map(prev);
        next.set(tin, { ...supplier, category });
        return next;
      });
      await supabase.from('suppliers').update({ category }).eq('id', supplier.id);
      return;
    }
    // Supplier not registered yet — create it on the fly so the category sticks.
    const { data, error } = await supabase
      .from('suppliers')
      .insert({
        client_id: clientId,
        tin,
        name: supplierName ?? '',
        display_name: supplierName ?? tin,
        category,
      })
      .select('id')
      .single();
    if (!error && data) {
      setSuppliersByTin((prev) => new Map(prev).set(tin, { id: data.id, category }));
    }
  }

  async function loadProfileNames(ids: string[]): Promise<Map<string, string>> {
    const unique = [...new Set(ids.filter(Boolean))];
    if (!supabase || unique.length === 0) return new Map();
    const { data } = await supabase
      .from('profiles')
      .select('id, full_name, username, email')
      .in('id', unique);
    const map = new Map<string, string>();
    for (const p of data ?? []) {
      const label = profileLabel(p);
      if (label) map.set(p.id, label);
    }
    return map;
  }

  async function loadApprovedIds() {
    if (!supabase || !clientId) return;
    type InvoiceRow = {
      id: string;
      tax_invoice_id: string;
      internal_status: string;
      approved_by_user_id: string | null;
      approved_by_name: string | null;
      reverted_by_user_id: string | null;
      reverted_by_name: string | null;
      approved_at: string | null;
      approval_note: string | null;
    };
    // Cast needed: columns added across migrations 0015 / 0072 — not all in generated types.
    const { data } = await (supabase
      .from('invoices')
      .select(
        'id, tax_invoice_id, internal_status, approved_by_user_id, approved_by_name, reverted_by_user_id, reverted_by_name, approved_at, approval_note',
      )
      .eq('client_id', clientId) as unknown as PromiseLike<{ data: InvoiceRow[] | null }>);
    if (!data) return;

    const userIds = data.flatMap((r) => [r.approved_by_user_id, r.reverted_by_user_id].filter(Boolean));
    const profileMap = await loadProfileNames(userIds as string[]);

    const { data: staffRows } = await supabase
      .from('profiles')
      .select('full_name, username, email')
      .eq('client_id', clientId)
      .in('role', ['owner', 'admin', 'manager']);

    const nameSet = new Set<string>();
    for (const p of staffRows ?? []) {
      const label = profileLabel(p);
      if (label) nameSet.add(label);
    }
    for (const r of data) {
      const approved = resolvePersonName(
        r.approved_by_name as string | null,
        r.approved_by_user_id as string | null,
        profileMap,
      );
      const reverted = resolvePersonName(
        r.reverted_by_name as string | null,
        r.reverted_by_user_id as string | null,
        profileMap,
      );
      if (approved) nameSet.add(approved);
      if (reverted) nameSet.add(reverted);
    }
    setApproverNames([...nameSet].sort((a, b) => a.localeCompare(b)));

    setApprovedIds(new Set(data.map((r) => r.tax_invoice_id as string)));
    setInvoiceMeta(
      new Map(
        data.map((r) => [
          r.tax_invoice_id as string,
          {
            status: r.internal_status as 'approved' | 'reverted' | 'pending',
            approvedBy: resolvePersonName(
              r.approved_by_name as string | null,
              r.approved_by_user_id as string | null,
              profileMap,
            ),
            revertedBy: resolvePersonName(
              r.reverted_by_name as string | null,
              r.reverted_by_user_id as string | null,
              profileMap,
            ),
            approvedAt: r.approved_at as string | null,
            approvalNote: r.approval_note as string | null,
            dbId: r.id as string,
          },
        ]),
      ),
    );
  }

  // ─── Fetch invoice list from Netlify → tax service ────────────────────────

  /** Returns true if the error string indicates an expired/invalid JWT. */
  function isJwtExpiredError(msg: string | undefined): boolean {
    if (!msg) return false;
    const lower = msg.toLowerCase();
    return (
      lower.includes('jwt') ||
      lower.includes('parse jwt') ||
      lower.includes('token expired') ||
      lower.includes('unauthorized')
    );
  }

  // pageOffset is a 1-based row offset. pageLimit=25 matches the tax service FE.
  // Next offset = current offset + pageLimit (1 → 26 → 51 …).
  const PAGE_LIMIT = 25;
  /** Pause between Netlify list calls — each triggers 2 upstream tax requests. */
  const BETWEEN_PAGES_MS = 400;

  function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /** Parse raw tax-service items into TaxInvoice objects. */
  function parseItems(raw: Record<string, unknown>[]): TaxInvoice[] {
    return raw.map((i) => ({
      id: String(i['id'] ?? ''),
      serialNo: String(i['serialNo'] ?? ''),
      supplierTin: String(i['supplierTin'] ?? ''),
      supplierName: String(i['supplierName'] ?? ''),
      status: String(i['status'] ?? ''),
      type: String(i['type'] ?? i['invoiceType'] ?? ''),
      issuedAt: String(i['issuedAt'] ?? ''),
      totalValue: Number(i['totalValue'] ?? 0),
      totalVatAmount: Number(i['totalVatAmount'] ?? i['vatAmount'] ?? 0),
      total: Number(i['total'] ?? i['totalValue'] ?? 0),
    }));
  }

  /** Fetch one page. pageOffset is 1-based row offset. */
  async function fetchPage(
    pageToken: string,
    pageTin: string,
    from: string,
    to: string,
    pageOffset: number,
    supplierTinFilter: string,
  ): Promise<Record<string, unknown>[]> {
    const res = await fetch(netlifyFn('get-invoice-list'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token: pageToken,
        tin: pageTin,
        dateFrom: from,
        dateTo: to,
        pageOffset,
        pageLimit: PAGE_LIMIT,
        ...(supplierTinFilter ? { supplierTin: supplierTinFilter } : {}),
      }),
    });
    const json: {
      ok?: boolean;
      payload?: unknown[] | { items?: unknown[] };
      total?: number;
      error?: string;
      failure?: { code?: string; message?: string };
    } = await res.json();
    const errMsg =
      json.error ??
      (json.ok === false && json.failure?.message ? json.failure.message : undefined) ??
      (!res.ok ? `HTTP ${res.status}` : undefined);
    if (errMsg) throw Object.assign(new Error(errMsg), { status: res.status });
    const raw = json.payload;
    const items = (Array.isArray(raw) ? raw : ((raw as { items?: unknown[] })?.items ?? [])) as Record<
      string,
      unknown
    >[];
    const issued = items
      .map((i) => String(i['issuedAt'] ?? ''))
      .filter(Boolean)
      .sort();
    console.info(
      `[invoices] ${from}..${to} offset=${pageOffset} supplier=${supplierTinFilter || '-'} ` +
        `returned=${items.length} apiTotal=${json.total ?? '?'} ` +
        `issuedAt=${issued[0] ?? 'none'}..${issued[issued.length - 1] ?? 'none'}`,
    );
    return items;
  }

  /** Deduplicate + append a page of raw items into the accumulator and publish. */
  function appendItems(items: Record<string, unknown>[]) {
    const p = paginationRef.current;
    const newItems = items.filter((i) => {
      const id = String(i['id'] ?? '');
      if (p.seenIds.has(id)) return false;
      p.seenIds.add(id);
      return true;
    });
    if (newItems.length > 0) {
      p.allItems.push(...newItems);
      setInvoices(parseItems(p.allItems));
    }
  }

  // Fetch every page for the current date range (1 → 26 → 51 …) until a short page.
  async function fetchInvoices(retrying = false) {
    if (!token || !tin) return;
    setListLoading(true);
    setListError(null);
    setListLoadedCount(0);

    await Promise.all([loadApprovedIds(), loadSuppliers()]);

    const p = paginationRef.current;
    const gen = ++p.fetchGen;
    p.offset = 1;
    p.seenIds = new Set<string>();
    p.allItems = [];
    p.token = token;
    p.tin = tin;
    p.from = dateFrom;
    p.to = dateTo;
    setInvoices([]);

    try {
      // The tax API rejects spans longer than 6 months, so page each window.
      const windows = taxDateWindows(dateFrom, dateTo);
      console.info(
        `[invoices] start ${dateFrom}..${dateTo} supplier=${supplierTin || '-'} windows=${windows
          .map((w) => `${w.from}..${w.to}`)
          .join(', ')}`,
      );
      for (let w = 0; w < windows.length; w++) {
        const span = windows[w]!;
        let offset = 1;
        let pages = 0;
        while (true) {
          if (paginationRef.current.fetchGen !== gen) return;
          const items = await fetchPage(token, tin, span.from, span.to, offset, supplierTin);
          if (paginationRef.current.fetchGen !== gen) return;
          appendItems(items);
          setListLoadedCount(paginationRef.current.allItems.length);
          pages += 1;
          offset += PAGE_LIMIT;
          if (items.length < PAGE_LIMIT) break;
          await sleep(BETWEEN_PAGES_MS);
        }
        console.info(
          `[invoices] window ${span.from}..${span.to} pages=${pages} runningTotal=${paginationRef.current.allItems.length}`,
        );
        if (w < windows.length - 1) await sleep(BETWEEN_PAGES_MS);
      }
      const issued = paginationRef.current.allItems
        .map((i) => String(i['issuedAt'] ?? ''))
        .filter(Boolean)
        .sort();
      console.info(
        `[invoices] done count=${paginationRef.current.allItems.length} ` +
          `issuedAt=${issued[0] ?? 'none'}..${issued[issued.length - 1] ?? 'none'}`,
      );
      const openId = pendingOpenId.current;
      if (openId) {
        const match = parseItems(paginationRef.current.allItems).find((inv) => inv.id === openId);
        if (match) {
          pendingOpenId.current = null;
          void openReview(match);
        }
      }
    } catch (err) {
      if (paginationRef.current.fetchGen !== gen) return;
      const e = err as Error & { status?: number };
      console.error(
        `[invoices] failed ${dateFrom}..${dateTo} supplier=${supplierTin || '-'} loaded=${paginationRef.current.allItems.length}: ${e.message}`,
      );
      if (!retrying && (e.status === 401 || isJwtExpiredError(e.message))) {
        clearCachedToken();
        setToken(null);
        setTin('');
        setListLoading(false);
        await loadToken();
        return;
      }
      setListError(e.message);
    } finally {
      if (paginationRef.current.fetchGen === gen) {
        setListLoading(false);
        hasFetched.current = true;
      }
    }
  }

  // ─── Load previous goods-name mappings for auto-suggest ───────────────────

  async function loadPrevMappings(supplierTin: string) {
    if (!supabase || !clientId) return;
    prevMappings.current.clear();

    const ingMap = new Map<string, IngredientOption>();
    async function ensureIngredients(ids: string[]) {
      const missing = ids.filter((id) => !ingMap.has(id));
      if (missing.length === 0 || !supabase) return;
      const { data: ings } = await supabase
        .from('ingredients')
        .select('id, name, unit, cost_price')
        .in('id', missing);
      for (const ing of ings ?? []) ingMap.set(ing.id, ing as IngredientOption);
    }

    const { data: templates } = await (supabase
      .from('supplier_goods_splits')
      .select(
        'goods_name, supplier_goods_split_lines(ingredient_id, qty_per_source_unit, sort_order)',
      )
      .eq('client_id', clientId)
      .eq('supplier_tin', supplierTin ?? '') as unknown as PromiseLike<{
      data:
        | {
            goods_name: string;
            supplier_goods_split_lines: {
              ingredient_id: string;
              qty_per_source_unit: number;
              sort_order: number;
            }[];
          }[]
        | null;
    }>);

    for (const tpl of templates ?? []) {
      const key = mappingKey(supplierTin, tpl.goods_name);
      if (prevMappings.current.has(key)) continue;
      const lines = [...(tpl.supplier_goods_split_lines ?? [])].sort(
        (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
      );
      if (lines.length === 0) continue;
      await ensureIngredients(lines.map((l) => l.ingredient_id));
      const splits = lines
        .map((l) => {
          const ing = ingMap.get(l.ingredient_id);
          if (!ing) return null;
          return { ingredient: ing, qtyPerSourceUnit: Number(l.qty_per_source_unit ?? 0) };
        })
        .filter(Boolean) as { ingredient: IngredientOption; qtyPerSourceUnit: number }[];
      if (splits.length > 0) prevMappings.current.set(key, { mode: 'split', splits });
    }

    const { data: legacyRows } = await (supabase
      .from('invoice_goods')
      .select('goods_name, ingredient_id, quantity, source_quantity, invoice_id')
      .eq('client_id', clientId)
      .not('ingredient_id', 'is', null)
      .order('id', { ascending: false }) as unknown as PromiseLike<{
      data:
        | {
            goods_name: string;
            ingredient_id: string | null;
            quantity: number;
            source_quantity: number;
            invoice_id: string;
          }[]
        | null;
    }>);

    const invoiceSupplierById = new Map<string, string>();
    const invoiceApprovedAtById = new Map<string, string>();
    const legacyInvoiceIds = [...new Set((legacyRows ?? []).map((r) => r.invoice_id))];
    if (legacyInvoiceIds.length > 0 && supabase) {
      const { data: invRows } = await (supabase
        .from('invoices')
        .select('id, supplier_tin, approved_at')
        .in('id', legacyInvoiceIds) as unknown as PromiseLike<{
        data: { id: string; supplier_tin: string | null; approved_at: string | null }[] | null;
      }>);
      for (const inv of invRows ?? []) {
        invoiceSupplierById.set(inv.id, inv.supplier_tin ?? '');
        if (inv.approved_at) invoiceApprovedAtById.set(inv.id, inv.approved_at);
      }
    }

    // Most recently approved first — invoice_goods.id is a UUID, so ordering by
    // it is arbitrary and could pick a stale mapping (e.g. a pre-unit-change row).
    const sortedLegacyRows = [...(legacyRows ?? [])].sort((a, b) =>
      (invoiceApprovedAtById.get(b.invoice_id) ?? '').localeCompare(
        invoiceApprovedAtById.get(a.invoice_id) ?? '',
      ),
    );

    const legacyIds = sortedLegacyRows.map((r) => r.ingredient_id).filter(Boolean) as string[];
    await ensureIngredients(legacyIds);

    for (const row of sortedLegacyRows) {
      const goodsName = row.goods_name;
      if (!goodsName || !row.ingredient_id) continue;
      const ing = ingMap.get(row.ingredient_id);
      if (!ing) continue;
      const srcQty = Number(row.source_quantity ?? 0);
      const txQty = Number(row.quantity ?? 0);
      const ratio = srcQty > 0 ? txQty / srcQty : 1;

      const supplierKey = mappingKey(supplierTin, goodsName);
      if (!prevMappings.current.has(supplierKey)) {
        const rowSupplier = invoiceSupplierById.get(row.invoice_id) ?? '';
        if (rowSupplier === (supplierTin ?? '')) {
          prevMappings.current.set(supplierKey, { mode: 'single', ingredient: ing, ratio });
        }
      }

      const globalKey = mappingKey('', goodsName);
      if (!prevMappings.current.has(globalKey)) {
        prevMappings.current.set(globalKey, { mode: 'single', ingredient: ing, ratio });
      }
    }
  }

  // ─── Open review panel for one invoice ────────────────────────────────────

  /** Build GoodsRow[] from raw tax-service items + current prevMappings. */
  function buildGoodsRows(goodsItems: Record<string, unknown>[], supplierTin: string): GoodsRow[] {
    return goodsItems.map((g) => {
      const name = String(g['name'] ?? '');
      const prev =
        prevMappings.current.get(mappingKey(supplierTin, name)) ??
        prevMappings.current.get(mappingKey('', name));
      const invoiceQty = Number(g['quantity'] ?? 0);
      const total = Number(g['total'] ?? g['totalValue'] ?? 0);
      const unitPrice = invoiceQty > 0 ? total / invoiceQty : 0;

      if (prev?.mode === 'split') {
        const splits = prev.splits.map((s) => {
          const txQty = Math.round(s.qtyPerSourceUnit * invoiceQty * 10000) / 10000;
          return {
            ingredientId: s.ingredient.id,
            newIngredientName: '',
            useExisting: true,
            txQuantity: txQty,
            txQuantityStr: String(txQty),
          };
        });
        const txSum = splits.reduce((sum, s) => sum + s.txQuantity, 0);
        return {
          goodsName: name,
          quantity: invoiceQty,
          txQuantity: txSum,
          txQuantityStr: String(txSum),
          unit: String(g['unit'] ?? ''),
          unitPrice,
          total,
          ingredientId: null,
          newIngredientName: '',
          newIngredientUnit: String(g['unit'] ?? 'kg'),
          useExisting: true,
          skip: false,
          splitMode: true,
          splits,
        };
      }

      const autoTxQty =
        prev?.mode === 'single'
          ? Math.round(prev.ratio * invoiceQty * 10000) / 10000
          : invoiceQty;
      return {
        goodsName: name,
        quantity: invoiceQty,
        txQuantity: autoTxQty,
        txQuantityStr: String(autoTxQty),
        unit: String(g['unit'] ?? ''),
        unitPrice,
        total,
        ingredientId: prev?.mode === 'single' ? prev.ingredient.id : null,
        newIngredientName: '',
        newIngredientUnit: String(g['unit'] ?? 'kg'),
        useExisting: true,
        skip: false,
        splitMode: false,
        splits: [],
      };
    });
  }

  // ─── Saved goods for approved invoices ─────────────────────────────────────
  // On re-open of an approved invoice, show the quantities/links that were
  // actually saved on approval — not the prevMappings heuristic.

  type SavedGoodsLine = {
    goods_name: string;
    quantity: number;
    ingredient_id: string | null;
    line_index: number | null;
  };

  async function fetchSavedGoods(taxInvoiceId: string): Promise<SavedGoodsLine[] | null> {
    if (!supabase || !clientId) return null;
    const { data: inv } = await (supabase
      .from('invoices')
      .select('id')
      .eq('client_id', clientId)
      .eq('tax_invoice_id', taxInvoiceId)
      .maybeSingle() as unknown as PromiseLike<{ data: { id: string } | null }>);
    if (!inv) return null;
    const { data } = await (supabase
      .from('invoice_goods')
      .select('goods_name, quantity, ingredient_id, line_index')
      .eq('invoice_id', inv.id)
      .order('id', { ascending: true }) as unknown as PromiseLike<{
      data: SavedGoodsLine[] | null;
    }>);
    return data ?? null;
  }

  /** Override heuristic rows with the quantities/links saved at approval time. */
  function applySavedGoods(rows: GoodsRow[], saved: SavedGoodsLine[]): GoodsRow[] {
    const byIndex = new Map<number, SavedGoodsLine[]>();
    const unindexed: SavedGoodsLine[] = [];
    for (const s of saved) {
      if (s.line_index == null) {
        unindexed.push(s);
      } else {
        const arr = byIndex.get(s.line_index) ?? [];
        arr.push(s);
        byIndex.set(s.line_index, arr);
      }
    }

    return rows.map((row, i) => {
      let lines = byIndex.get(i);
      if (!lines || lines.length === 0) {
        // Legacy rows without line_index — match by goods name, consuming each once.
        const idx = unindexed.findIndex((s) => s.goods_name === row.goodsName);
        if (idx >= 0) lines = unindexed.splice(idx, 1);
      }
      if (!lines || lines.length === 0) return row;

      if (lines.length === 1) {
        const s = lines[0]!;
        const qty = Number(s.quantity ?? 0);
        return {
          ...row,
          skip: s.ingredient_id === null,
          splitMode: false,
          splits: [],
          txQuantity: qty,
          txQuantityStr: String(qty),
          ingredientId: s.ingredient_id,
          useExisting: true,
          newIngredientName: '',
        };
      }

      // Multiple saved rows for one invoice line = split across ingredients.
      const splits = lines.map((s) => {
        const qty = Number(s.quantity ?? 0);
        return {
          ingredientId: s.ingredient_id,
          newIngredientName: '',
          useExisting: true,
          txQuantity: qty,
          txQuantityStr: String(qty),
        };
      });
      const txSum = splits.reduce((sum, s) => sum + s.txQuantity, 0);
      return {
        ...row,
        skip: false,
        splitMode: true,
        splits,
        txQuantity: txSum,
        txQuantityStr: String(txSum),
        ingredientId: null,
      };
    });
  }

  async function buildReviewRows(
    invoice: TaxInvoice,
    goodsItems: Record<string, unknown>[],
    supplierTin: string,
  ): Promise<GoodsRow[]> {
    let rows = buildGoodsRows(goodsItems, supplierTin);
    // Approved: show what was saved. Reverted: prefill from the last approval
    // (rows survive revert) so re-approving starts from the real quantities.
    const status = internalStatus(invoice.id);
    if (status === 'approved' || status === 'reverted') {
      const saved = await fetchSavedGoods(invoice.id);
      if (saved && saved.length > 0) rows = applySavedGoods(rows, saved);
    }
    return rows;
  }

  async function loadReviewGoods(invoice: TaxInvoice, opts?: { skipCache?: boolean }) {
    if (!token) return;
    setGoodsLoad(true);
    setGoodsError(null);

    const supplierTin = invoice.supplierTin ?? '';

    if (opts?.skipCache) {
      rawGoodsCache.current.delete(invoice.id);
    } else {
      const cached = rawGoodsCache.current.get(invoice.id);
      if (cached) {
        setRawInvoice(invoice as unknown as Record<string, unknown>);
        setGoods(await buildReviewRows(invoice, cached, supplierTin));
        setGoodsLoad(false);
        return;
      }
    }

    try {
      const res = await fetch(netlifyFn('get-invoice-goods'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, invoiceId: invoice.id, type: invoice.type }),
      });
      const json: { ok?: boolean; payload?: unknown; error?: string } = await res.json();

      if (!res.ok || (json as { error?: string }).error) {
        setGoodsError((json as { error?: string }).error ?? `HTTP ${res.status}`);
        return;
      }

      setRawInvoice(invoice as unknown as Record<string, unknown>);

      const goodsItems = (Array.isArray(json.payload) ? json.payload : []) as Record<
        string,
        unknown
      >[];

      rawGoodsCache.current.set(invoice.id, goodsItems);
      setGoods(await buildReviewRows(invoice, goodsItems, supplierTin));
    } catch (err) {
      setGoodsError((err as Error).message);
    } finally {
      setGoodsLoad(false);
    }
  }

  async function openReview(invoice: TaxInvoice) {
    if (!token) return;
    setReviewing(invoice);
    setGoods([]);
    setRawInvoice(null);
    setGoodsError(null);
    setApproveMsg(null);
    setConfirming(false);
    setApprovalNote('');

    const supplierTin = invoice.supplierTin ?? '';

    const [ingrRes] = await Promise.all([
      supabase
        ? supabase.from('ingredients').select('id, name, unit, cost_price').order('name')
        : Promise.resolve({ data: [] }),
      loadPrevMappings(supplierTin),
    ]);
    setIngredients((ingrRes.data ?? []) as IngredientOption[]);

    await loadReviewGoods(invoice);
  }

  // ─── Approve invoice ──────────────────────────────────────────────────────

  async function approve() {
    if (!supabase || !reviewing) return;
    setApproving(true);
    setApproveMsg(null);

    const payload = {
      taxInvoiceId: reviewing.id,
      serialNo: reviewing.serialNo,
      supplierTin: reviewing.supplierTin,
      supplierName: reviewing.supplierName,
      taxStatus: reviewing.status,
      issuedAt: reviewing.issuedAt,
      totalValue: reviewing.totalValue,
      totalVat: reviewing.totalVatAmount,
      total: reviewing.total,
      rawData: rawInvoice ?? {},
      approvalNote: approvalNote.trim() || null,
      goods: goods.map((g, lineIndex) => {
        const invoiceUnitPrice = g.quantity > 0 ? g.total / g.quantity : 0;
        if (g.skip) {
          return {
            goodsName: g.goodsName,
            quantity: g.txQuantity,
            sourceQuantity: g.quantity,
            unit: g.unit,
            unitPrice: g.txQuantity > 0 ? g.total / g.txQuantity : 0,
            total: g.total,
            skip: true,
            lineIndex,
            ingredientId: null,
            newIngredient: null,
          };
        }
        if (g.splitMode && g.splits.length > 0) {
          return {
            goodsName: g.goodsName,
            quantity: 0,
            sourceQuantity: g.quantity,
            unit: g.unit,
            unitPrice: invoiceUnitPrice,
            total: g.total,
            skip: false,
            lineIndex,
            ingredientId: null,
            newIngredient: null,
            splits: g.splits
              .filter((s) => s.txQuantity > 0)
              .map((s) => ({
                quantity: s.txQuantity,
                unit: s.useExisting
                  ? (ingredients.find((ing) => ing.id === s.ingredientId)?.unit ?? g.unit)
                  : g.newIngredientUnit,
                unitPrice: invoiceUnitPrice,
                total: invoiceUnitPrice * s.txQuantity,
                ingredientId: s.useExisting ? (s.ingredientId ?? null) : null,
                newIngredient:
                  !s.useExisting && s.newIngredientName.trim()
                    ? { name: s.newIngredientName.trim(), unit: g.newIngredientUnit }
                    : null,
              })),
          };
        }
        return {
          goodsName: g.goodsName,
          quantity: g.txQuantity,
          sourceQuantity: g.quantity,
          unit: g.useExisting
            ? (ingredients.find((ing) => ing.id === g.ingredientId)?.unit ?? g.unit)
            : g.newIngredientUnit,
          unitPrice: g.txQuantity > 0 ? g.total / g.txQuantity : 0,
          total: g.total,
          skip: false,
          lineIndex,
          ingredientId: g.useExisting ? (g.ingredientId ?? null) : null,
          newIngredient:
            !g.useExisting && g.newIngredientName.trim()
              ? { name: g.newIngredientName.trim(), unit: g.newIngredientUnit }
              : null,
        };
      }),
    };

    const { data, error } = await supabase.functions.invoke('approve-invoice', { body: payload });
    setApproving(false);

    if (error) {
      setApproveMsg({ text: error.message, ok: false });
      return;
    }

    const d = data as { error?: string; approved?: boolean; alreadyApproved?: boolean };
    if (d.error) {
      setApproveMsg({ text: d.error, ok: false });
      return;
    }

    setApproveMsg({ text: t('invoices.approvedMsg'), ok: true });
    // Optimistically update invoiceMeta so the row reflects approved immediately
    setInvoiceMeta((prev) => {
      const next = new Map(prev);
      const existing = next.get(reviewing.id);
      if (existing) {
        next.set(reviewing.id, {
          ...existing,
          status: 'approved',
          revertedBy: null,
          approvalNote: approvalNote.trim() || null,
        });
      }
      return next;
    });
    setApprovedIds((prev) => new Set([...prev, reviewing.id]));
    void loadApprovedIds();
    // Close panel after short delay so user sees success message.
    setTimeout(() => setReviewing(null), 1200);
  }

  // ─── Revert invoice ───────────────────────────────────────────────────────

  async function revertInvoice(taxInvoiceId: string) {
    if (!supabase) return;
    const meta = invoiceMeta.get(taxInvoiceId);
    if (!meta) return;
    setReverting(taxInvoiceId);
    setRevertMsg(null);
    const { data, error } = await supabase.functions.invoke('revert-invoice', {
      body: { invoiceId: meta.dbId },
    });
    setReverting(null);
    if (error) {
      setRevertMsg({ taxId: taxInvoiceId, text: error.message, ok: false });
      return;
    }
    const d = data as { error?: string; reverted?: boolean };
    if (d.error) {
      setRevertMsg({ taxId: taxInvoiceId, text: d.error, ok: false });
      return;
    }
    void loadApprovedIds();
  }

  // ─── Fetch goods for accordion row (read-only, cached) ───────────────────

  async function toggleRow(taxInvoiceId: string) {
    if (expandedId === taxInvoiceId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(taxInvoiceId);
    if (rowGoods.has(taxInvoiceId)) return;
    if (!token) return;

    setRowGoods((prev) => new Map(prev).set(taxInvoiceId, null));
    const invType = invoices.find((inv) => inv.id === taxInvoiceId)?.type ?? '';
    try {
      const res = await fetch(netlifyFn('get-invoice-goods'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, invoiceId: taxInvoiceId, type: invType }),
      });
      const json: { ok?: boolean; payload?: unknown; error?: string } = await res.json();
      if (!res.ok || json.error) {
        throw new Error(json.error ?? `HTTP ${res.status}`);
      }
      const items = (Array.isArray(json.payload) ? json.payload : []) as Record<string, unknown>[];
      setRowGoods((prev) =>
        new Map(prev).set(
          taxInvoiceId,
          items.map((g) => {
            const qty = Number(g['quantity'] ?? 0);
            const total = Number(g['total'] ?? g['totalValue'] ?? 0);
            return {
              name: String(g['name'] ?? ''),
              quantity: qty,
              unit: String(g['unit'] ?? ''),
              unitPrice: qty > 0 ? total / qty : 0,
              total,
            };
          }),
        ),
      );
    } catch {
      setRowGoods((prev) => new Map(prev).set(taxInvoiceId, []));
    }
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const formatDate = (iso: string) => (iso ? new Date(iso).toLocaleDateString() : '—');
  const formatAMD = (n: number) => n.toLocaleString('hy-AM', { maximumFractionDigits: 0 }) + ' ֏';
  // Unit prices can be fractional (e.g. 12.8 ֏/g) — show up to 4 decimal places,
  // trimming trailing zeros so 12.0000 shows as 12 and 12.8000 shows as 12.8.
  const formatUnitPrice = (n: number) =>
    n.toLocaleString('hy-AM', { minimumFractionDigits: 0, maximumFractionDigits: 4 }) + ' ֏';
  const internalStatus = (taxId: string) =>
    invoiceMeta.get(taxId)?.status ?? (approvedIds.has(taxId) ? 'approved' : 'pending');

  function updateGoods(i: number, patch: Partial<GoodsRow>) {
    setGoods((prev) => prev.map((r, ri) => (ri === i ? { ...r, ...patch } : r)));
  }

  function updateGoodsSplit(goodsIndex: number, splitIndex: number, patch: Partial<GoodsSplitLine>) {
    setGoods((prev) =>
      prev.map((row, ri) => {
        if (ri !== goodsIndex) return row;
        const splits = row.splits.map((s, si) => (si === splitIndex ? { ...s, ...patch } : s));
        const txSum = splits.reduce((sum, s) => sum + s.txQuantity, 0);
        return {
          ...row,
          splits,
          txQuantity: txSum,
          txQuantityStr: String(txSum),
        };
      }),
    );
  }

  function toggleSplitMode(i: number) {
    setGoods((prev) =>
      prev.map((g, ri) => {
        if (ri !== i) return g;
        if (g.splitMode) {
          const first = g.splits.find((s) => s.ingredientId || s.newIngredientName.trim());
          return {
            ...g,
            splitMode: false,
            splits: [],
            ingredientId: first?.ingredientId ?? g.ingredientId,
            newIngredientName: first?.newIngredientName ?? g.newIngredientName,
            useExisting: first?.useExisting ?? g.useExisting,
            txQuantity: splitQtySum(g) || g.txQuantity,
            txQuantityStr: String(splitQtySum(g) || g.txQuantity),
          };
        }
        const firstSplit: GoodsSplitLine = {
          ingredientId: g.ingredientId,
          newIngredientName: g.newIngredientName,
          useExisting: g.useExisting,
          txQuantity: g.txQuantity,
          txQuantityStr: g.txQuantityStr,
        };
        return {
          ...g,
          splitMode: true,
          splits: [firstSplit, blankSplitLine()],
        };
      }),
    );
  }

  function addGoodsSplit(goodsIndex: number) {
    setGoods((prev) =>
      prev.map((row, ri) =>
        ri === goodsIndex ? { ...row, splits: [...row.splits, blankSplitLine()] } : row,
      ),
    );
  }

  function removeGoodsSplit(goodsIndex: number, splitIndex: number) {
    setGoods((prev) =>
      prev.map((row, ri) => {
        if (ri !== goodsIndex) return row;
        const splits = row.splits.filter((_, si) => si !== splitIndex);
        const txSum = splits.reduce((sum, s) => sum + s.txQuantity, 0);
        return {
          ...row,
          splits: splits.length > 0 ? splits : [blankSplitLine()],
          txQuantity: txSum,
          txQuantityStr: String(txSum),
        };
      }),
    );
  }

  const filteredInvoices = invoices.filter((inv) => {
    if (filterSupplier && !inv.supplierName.toLowerCase().includes(filterSupplier.toLowerCase()))
      return false;
    if (filterSerial && !inv.serialNo.toLowerCase().includes(filterSerial.trim().toLowerCase()))
      return false;
    if (filterTotal) {
      const n = parseFloat(filterTotal.replace(/[^0-9.]/g, ''));
      if (!isNaN(n) && inv.total < n) return false;
      if (isNaN(n) && !formatAMD(inv.total).toLowerCase().includes(filterTotal.toLowerCase()))
        return false;
    }
    if (filterTaxStatus && inv.status !== filterTaxStatus) return false;
    const iStatus = internalStatus(inv.id);
    if (filterInternal && iStatus !== filterInternal) return false;
    if (filterApprovedBy) {
      const meta = invoiceMeta.get(inv.id);
      if (meta?.approvedBy !== filterApprovedBy) return false;
    }
    if (filterCategories.size > 0) {
      const cat = suppliersByTin.get(inv.supplierTin ?? '')?.category ?? '';
      const matches = filterCategories.has(cat);
      if (filterCategoryMode === 'include' && !matches) return false;
      if (filterCategoryMode === 'exclude' && matches) return false;
    }
    return true;
  });

  function exportCsv() {
    const escape = (v: string | number) => {
      const s = String(v ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };
    const headers = [
      t('invoices.colSerial'),
      t('invoices.csvSupplierTin'),
      t('invoices.colSupplier'),
      t('invoices.colDate'),
      t('invoices.csvTotalAmd'),
      t('invoices.colTaxStatus'),
      t('invoices.csvInternalStatus'),
      t('invoices.colApprovedBy'),
      t('invoices.csvApprovedAt'),
    ];
    const rows = filteredInvoices.map((inv) => {
      const m = invoiceMeta.get(inv.id);
      const istatus = m?.status ?? (approvedIds.has(inv.id) ? 'approved' : 'pending');
      return [
        escape(inv.serialNo || inv.id),
        escape(inv.supplierTin),
        escape(inv.supplierName),
        escape(inv.issuedAt ? new Date(inv.issuedAt).toLocaleDateString('en-GB') : ''),
        escape(inv.total),
        escape(taxStatusLabel(inv.status)),
        escape(internalStatusLabel(istatus)),
        escape(m?.revertedBy ? t('invoices.revertedBy', { name: m.revertedBy }) : (m?.approvedBy ?? '')),
        escape(m?.approvedAt ? new Date(m.approvedAt).toLocaleDateString('en-GB') : ''),
      ].join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoices-${dateFrom}-to-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  const hasFilters = Boolean(
    filterSupplier ||
      filterSerial ||
      filterCategories.size > 0 ||
      filterTotal ||
      filterTaxStatus ||
      filterInternal ||
      filterApprovedBy,
  );
  const resetFilters = () => {
    setFilterSupplier('');
    setFilterSerial('');
    setFilterCategories(new Set());
    setFilterCategoryMode('include');
    setCategoryFilterOpen(false);
    setFilterTotal('');
    setFilterTaxStatus('');
    setFilterInternal('');
    setFilterApprovedBy('');
  };
  const filteredTotal = filteredInvoices.reduce((sum, inv) => sum + inv.total, 0);
  const supplierSummaryTin = (() => {
    if (!filterSupplier.trim()) return null;
    const tins = [...new Set(filteredInvoices.map((i) => i.supplierTin).filter(Boolean))];
    return tins.length === 1 && suppliersByTin.has(tins[0]!) ? tins[0]! : null;
  })();
  const loadingLabel = tokenLoading
    ? t('invoices.connecting')
    : listLoading
      ? listLoadedCount > 0
        ? t('invoices.loadingCount', { count: listLoadedCount })
        : t('common.loading')
      : null;

  return (
    <section className="ui-stack inv-page">
      <PageHeader
        title={t('invoices.title')}
        subtitle={t('invoices.subtitle')}
        actions={
          filteredInvoices.length > 0 ? (
            <Button variant="secondary" icon={<Download />} onClick={exportCsv}>
              {t('common.exportCsv')}
            </Button>
          ) : undefined
        }
      />

      {tokenError && (
        <Callout
          tone="danger"
          actions={
            <Button variant="secondary" size="sm" onClick={loadToken} disabled={tokenLoading}>
              {t('common.retry')}
            </Button>
          }
        >
          {tokenError.includes('not configured') || tokenError.includes('credentials')
            ? t('invoices.tokenMissing')
            : tokenError}
        </Callout>
      )}

      {listError && (
        <Callout
          tone="danger"
          actions={
            <Button variant="secondary" size="sm" onClick={() => void fetchInvoices()} disabled={listLoading}>
              {t('common.retry')}
            </Button>
          }
        >
          {listError}
        </Callout>
      )}

      <div className="inv-toolbar">
        <DateRange value={range} onChange={setRange} size="sm" />
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          className="inv-supplier-tin"
          placeholder={t('invoices.supplierTin')}
          aria-label={t('invoices.supplierTin')}
          value={supplierTinInput}
          onChange={(e) => setSupplierTinInput(e.target.value.replace(/\D/g, ''))}
          onBlur={() => {
            const digits = supplierTinInput.replace(/\D/g, '');
            if (digits !== supplierTin) setSupplierTin(digits);
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            const digits = supplierTinInput.replace(/\D/g, '');
            if (digits !== supplierTin) setSupplierTin(digits);
          }}
        />
        {loadingLabel && <span className="muted inv-loading">{loadingLabel}</span>}
        <FilterSpacer />
        {filteredInvoices.length > 0 && (
          <div className="inv-summary">
            <span>
              {t('invoices.invoiceCount', { count: filteredInvoices.length })}
            </span>
            <span className="inv-summary-sep" />
            <span>
              {t('invoices.total', { amount: formatAMD(filteredTotal) })}
            </span>
            {supplierSummaryTin && (
              <>
                <span className="inv-summary-sep" />
                <Button
                  variant="text"
                  size="sm"
                  onClick={() => {
                    window.location.hash = `/suppliers?tin=${encodeURIComponent(supplierSummaryTin)}`;
                  }}
                >
                  {t('invoices.supplierSummary')}
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {invoices.length > 0 && (
        <Card layout="flush" className="inv-card">
          {/* Filters — one row */}
          <div className="inv-filters">
            <FilterSearch
              placeholder={t('invoices.supplier')}
              aria-label={t('invoices.supplier')}
              value={filterSupplier}
              onChange={(e) => setFilterSupplier(e.target.value)}
            />
            <FilterSearch
              placeholder={t('invoices.serial')}
              aria-label={t('invoices.serial')}
              value={filterSerial}
              onChange={(e) => setFilterSerial(e.target.value)}
            />
            <div className="inv-category-filter" ref={categoryFilterRef}>
              <button
                type="button"
                className={`ui-btn ui-btn--secondary ui-btn--sm inv-category-toggle${
                  filterCategories.size > 0 ? ' is-active' : ''
                }`}
                aria-expanded={categoryFilterOpen}
                onClick={() => setCategoryFilterOpen((v) => !v)}
              >
                {filterCategories.size === 0
                  ? t('invoices.category')
                  : `${filterCategoryMode === 'exclude' ? t('invoices.exclude') : t('invoices.include')} (${filterCategories.size})`}
                <ChevronDown />
              </button>
              {categoryFilterOpen && (
                <div className="inv-category-filter-panel ui-menu">
                  <SegmentedControl
                    size="sm"
                    ariaLabel={t('invoices.category')}
                    value={filterCategoryMode}
                    onChange={setFilterCategoryMode}
                    options={[
                      { value: 'include', label: t('invoices.include') },
                      { value: 'exclude', label: t('invoices.exclude') },
                    ]}
                  />
                  <div className="inv-category-filter-actions">
                    <Button variant="text" size="sm" onClick={() => setFilterCategories(new Set(SUPPLIER_CATEGORIES))}>
                      {t('invoices.selectAll')}
                    </Button>
                    <Button variant="text" size="sm" onClick={() => setFilterCategories(new Set())}>
                      {t('invoices.clear')}
                    </Button>
                  </div>
                  <ul className="inv-category-filter-list">
                    {SUPPLIER_CATEGORIES.map((c) => (
                      <li key={c}>
                        <label className="inv-category-filter-item">
                          <input
                            type="checkbox"
                            checked={filterCategories.has(c)}
                            onChange={() => {
                              setFilterCategories((prev) => {
                                const next = new Set(prev);
                                if (next.has(c)) next.delete(c);
                                else next.add(c);
                                return next;
                              });
                            }}
                          />
                          <span>{t(`suppliers.cat_${c}`)}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <input
              type="text"
              inputMode="numeric"
              className="inv-filter-amount"
              placeholder={t('invoices.minTotal')}
              aria-label={t('invoices.minTotal')}
              value={filterTotal}
              onChange={(e) => setFilterTotal(e.target.value)}
            />
            <select
              aria-label={t('invoices.colTaxStatus')}
              value={filterTaxStatus}
              onChange={(e) => setFilterTaxStatus(e.target.value)}
            >
              <option value="">{t('invoices.taxStatusAll')}</option>
              <option value="ISSUED">{t('invoices.taxIssued')}</option>
              <option value="APPROVED">{t('invoices.taxApproved')}</option>
            </select>
            <select
              aria-label={t('invoices.colInternal')}
              value={filterInternal}
              onChange={(e) => setFilterInternal(e.target.value)}
            >
              <option value="">{t('invoices.internalAll')}</option>
              <option value="pending">{t('invoices.pending')}</option>
              <option value="approved">{t('invoices.approved')}</option>
              <option value="reverted">{t('invoices.reverted')}</option>
            </select>
            <select
              aria-label={t('invoices.colApprovedBy')}
              value={filterApprovedBy}
              onChange={(e) => setFilterApprovedBy(e.target.value)}
            >
              <option value="">{t('invoices.approvedByAll')}</option>
              {approverNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            {hasFilters && (
              <Button variant="text" size="sm" icon={<X />} onClick={resetFilters}>
                {t('invoices.reset')}
              </Button>
            )}
          </div>

          {filteredInvoices.length === 0 ? (
            <EmptyState
              compact
              title={t('invoices.noMatchTitle')}
              text={t('invoices.noMatchText')}
              actions={
                <Button variant="secondary" size="sm" onClick={resetFilters}>
                  {t('invoices.resetFilters')}
                </Button>
              }
            />
          ) : (
            <div className="inv-list">
              <div className="inv-list-row inv-list-header">
                <div className="inv-row-num">{t('invoices.colNum')}</div>
                <div>{t('invoices.colSerial')}</div>
                <div>{t('invoices.colSupplier')}</div>
                <div className="inv-col-category">{t('invoices.colCategory')}</div>
                <div className="inv-col-date">{t('invoices.colDate')}</div>
                <div className="inv-col-total num">{t('invoices.colTotal')}</div>
                <div className="inv-col-tax">{t('invoices.colTaxStatus')}</div>
                <div className="inv-col-internal">{t('invoices.colInternal')}</div>
                <div className="inv-col-approved-by">{t('invoices.colApprovedBy')}</div>
                <div />
              </div>

              {filteredInvoices.map((inv, idx) => {
                const istatus = internalStatus(inv.id);
                const m = invoiceMeta.get(inv.id);
                return (
                  <div className="inv-list-row" key={inv.id}>
                    <div className="inv-row-num">{idx + 1}</div>
                    <div>
                      <code className="inv-serial">{inv.serialNo || inv.id.slice(0, 8)}</code>
                    </div>
                    <div>
                      <div className="inv-supplier">{inv.supplierName}</div>
                      <div className="inv-row-meta">
                        <span className="inv-row-meta-dt">
                          {inv.serialNo || inv.id.slice(0, 8)} · {formatDate(inv.issuedAt)} · {formatAMD(inv.total)}
                        </span>
                        <span className="inv-row-meta-badges">
                          <StatusPill status={inv.status} dot={false}>{taxStatusLabel(inv.status)}</StatusPill>
                          <StatusPill status={istatus} dot={false}>{internalStatusLabel(istatus)}</StatusPill>
                        </span>
                        {m && (m.revertedBy || m.approvedBy) && (
                          <span className="inv-row-meta-badges">
                            {m.revertedBy ? t('invoices.revertedBy', { name: m.revertedBy }) : m.approvedBy}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="inv-col-category">
                      {inv.supplierTin ? (
                        <select
                          className="inline-select"
                          aria-label={t('invoices.colCategory')}
                          value={suppliersByTin.get(inv.supplierTin)?.category ?? ''}
                          onChange={(e) =>
                            void updateSupplierCategory(inv.supplierTin, e.target.value, inv.supplierName)
                          }
                        >
                          <option value="">{t('invoices.uncategorised')}</option>
                          {SUPPLIER_CATEGORIES.map((c) => (
                            <option key={c} value={c}>
                              {t(`suppliers.cat_${c}`)}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </div>
                    <div className="inv-col-date muted">{formatDate(inv.issuedAt)}</div>
                    <div className="inv-col-total num">{formatAMD(inv.total)}</div>
                    <div className="inv-col-tax">
                      <StatusPill status={inv.status}>{taxStatusLabel(inv.status)}</StatusPill>
                    </div>
                    <div className="inv-col-internal">
                      <StatusPill status={istatus} tone={istatus === 'reverted' ? 'warning' : undefined}>
                        {internalStatusLabel(istatus)}
                      </StatusPill>
                    </div>
                    <div className="inv-col-approved-by">
                      {m?.revertedBy ? (
                        <>
                          <span className="text-warning">{t('invoices.reverted')}</span>
                          <span>{m.revertedBy}</span>
                        </>
                      ) : m?.approvedBy ? (
                        <>
                          <span>{m.approvedBy}</span>
                          {m.approvedAt && <span>{formatDate(m.approvedAt)}</span>}
                          {m.approvalNote && (
                            <span className="inv-approval-note" title={m.approvalNote}>
                              {m.approvalNote}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </div>
                    <div className="inv-row-actions">
                      {confirmingRevert !== inv.id && (
                        <Button variant="secondary" size="sm" onClick={() => openReview(inv)}>
                          {t('invoices.review')}
                        </Button>
                      )}
                      {istatus === 'approved' &&
                        (reverting === inv.id ? (
                          <span className="muted">{t('common.saving')}</span>
                        ) : revertMsg?.taxId === inv.id && !revertMsg.ok ? (
                          <span className="text-danger">{revertMsg.text}</span>
                        ) : confirmingRevert === inv.id ? (
                          <div className="inv-revert-confirm">
                            <span className="muted">{t('invoices.revertConfirm')}</span>
                            <Button variant="ghost" size="sm" onClick={() => setConfirmingRevert(null)}>
                              {t('common.cancel')}
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => {
                                setConfirmingRevert(null);
                                void revertInvoice(inv.id);
                              }}
                            >
                              {t('common.yes')}
                            </Button>
                          </div>
                        ) : (
                          <Button variant="destructive" size="sm" onClick={() => setConfirmingRevert(inv.id)}>
                            {t('invoices.revert')}
                          </Button>
                        ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {invoices.length === 0 && !listLoading && !listError && hasFetched.current && (
        <Card>
          <EmptyState
            icon={<FileText />}
            title={t('invoices.emptyTitle')}
            text={t('invoices.emptyText')}
          />
        </Card>
      )}

      {/* ── Review / Approval modal ──────────────────────────────────────── */}
      {reviewing &&
        (() => {
          const reviewIstatus = internalStatus(reviewing.id);
          const isReadOnly = reviewIstatus === 'approved';
          const reviewMeta = invoiceMeta.get(reviewing.id);
          return (
            <div
              className="modal-overlay"
              onClick={(e) => {
                if (e.target === e.currentTarget) setReviewing(null);
              }}
            >
              <div
                className="modal modal-wide"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: '92vh',
                  maxWidth: 1280,
                  width: '95vw',
                }}
              >
                <div className="modal-header">
                  <div>
                    <h2
                      style={{
                        margin: 0,
                        fontSize: 18,
                        textTransform: 'none',
                        letterSpacing: 0,
                        color: 'var(--text)',
                        fontWeight: 600,
                      }}
                    >
                      {t('invoices.reviewTitle')}
                    </h2>
                    <p style={{ margin: '3px 0 0', fontSize: 13 }}>
                      {reviewing.serialNo} · {reviewing.supplierName} ·{' '}
                      {formatDate(reviewing.issuedAt)}
                    </p>
                    {reviewMeta?.approvalNote && (
                      <p
                        style={{
                          margin: '6px 0 0',
                          fontSize: 12,
                          color: 'var(--text-muted)',
                          fontStyle: 'italic',
                        }}
                      >
                        {t('invoices.metaNote')}: {reviewMeta.approvalNote}
                      </p>
                    )}
                  </div>
                  <button className="btn-icon" onClick={() => setReviewing(null)}>
                    <X className="ui-i" />
                  </button>
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: 20,
                    alignItems: 'center',
                    marginBottom: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ fontSize: 13 }}>
                    {t('invoices.metaTotal')}: <strong>{formatAMD(reviewing.total)}</strong>
                  </span>
                  <span className="muted">{t('invoices.metaVat')}: {formatAMD(reviewing.totalVatAmount)}</span>
                  <span
                    className={`badge ${reviewing.status === 'ISSUED' ? 'badge-warning' : 'badge-success'}`}
                  >
                    {t('invoices.metaTax')}: {taxStatusLabel(reviewing.status)}
                  </span>
                </div>

                <p
                  style={{
                    margin: '0 0 16px',
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    borderBottom: '1px solid var(--border)',
                    paddingBottom: 12,
                  }}
                >
                  {t('invoices.approveHint')}
                </p>

                {goodsLoading && <div className="empty-state">{t('invoices.loadingGoods')}</div>}
                {goodsError && (
                  <div
                    className="error-banner"
                    style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                  >
                    <span>{goodsError}</span>
                    {reviewing && (
                      <button
                        type="button"
                        className="btn-ghost btn-sm"
                        onClick={() => void loadReviewGoods(reviewing, { skipCache: true })}
                        disabled={goodsLoading}
                      >
                        {t('common.retry')}
                      </button>
                    )}
                  </div>
                )}

                {/* Extra invoice details from the goods endpoint response */}
                {rawInvoice && !goodsLoading && (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '4px 20px',
                      fontSize: 12,
                      color: 'var(--text-muted)',
                      marginBottom: 12,
                      paddingBottom: 10,
                      borderBottom: '1px solid var(--border)',
                    }}
                  >
                    {!!rawInvoice['deliveryMethod'] && (
                      <span>
                        {t('invoices.delivery')}:{' '}
                        <strong style={{ color: 'var(--text)' }}>
                          {String(rawInvoice['deliveryMethod'])}
                        </strong>
                      </span>
                    )}
                    {!!rawInvoice['authPersonName'] && (
                      <span>
                        {t('invoices.authPerson')}:{' '}
                        <strong style={{ color: 'var(--text)' }}>
                          {String(rawInvoice['authPersonName'])}
                        </strong>
                      </span>
                    )}
                    {!!rawInvoice['sourceFullAddress'] && (
                      <span>
                        {t('invoices.shipFrom')}:{' '}
                        <strong style={{ color: 'var(--text)' }}>
                          {String(rawInvoice['sourceFullAddress'])}
                        </strong>
                      </span>
                    )}
                    {!!rawInvoice['destinationFullAddress'] && (
                      <span>
                        {t('invoices.shipTo')}:{' '}
                        <strong style={{ color: 'var(--text)' }}>
                          {String(rawInvoice['destinationFullAddress'])}
                        </strong>
                      </span>
                    )}
                    {!!rawInvoice['additionalInfo'] && (
                      <span style={{ gridColumn: '1 / -1' }}>
                        {t('invoices.metaNote')}:{' '}
                        <strong style={{ color: 'var(--text)' }}>
                          {String(rawInvoice['additionalInfo'])}
                        </strong>
                      </span>
                    )}
                  </div>
                )}

                {/* Scrollable goods table */}
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'auto',
                    marginLeft: -24,
                    marginRight: -24,
                    paddingLeft: 24,
                    paddingRight: 24,
                  }}
                >
                  {goods.length > 0 && (
                    <table className="table" style={{ fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th>{t('invoices.colTrack')}</th>
                          <th>{t('invoices.colGoodsName')}</th>
                          <th>{t('invoices.colInvoiceQty')}</th>
                          <th>{t('invoices.colInvoicePrice')}</th>
                          <th>{t('invoices.colRecordAs')}</th>
                          <th>{t('invoices.colUnit')}</th>
                          <th>{t('invoices.colPrice')}</th>
                          <th>{t('invoices.colTotal')}</th>
                          <th>{t('invoices.colLinkIngredient')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {goods.map((g, i) => {
                          const linkedUnit = g.useExisting
                            ? (ingredients.find((ing) => ing.id === g.ingredientId)?.unit ?? g.unit)
                            : g.newIngredientUnit;
                          const splitSum = splitQtySum(g);
                          const qtyMismatch =
                            g.splitMode && g.quantity > 0 && Math.abs(splitSum - g.quantity) > 0.0001;
                          return (
                            <Fragment key={i}>
                              <tr key={i} style={g.skip ? { opacity: 0.55 } : undefined}>
                                <td style={{ textAlign: 'center' }}>
                                  <input
                                    type="checkbox"
                                    checked={!g.skip}
                                    disabled={isReadOnly}
                                    title={t('invoices.trackTitle')}
                                    onChange={(e) => updateGoods(i, { skip: !e.target.checked })}
                                  />
                                </td>
                                <td>
                                  <div>{g.goodsName}</div>
                                  {!g.skip && !isReadOnly && (
                                    <button
                                      type="button"
                                      className="btn-ghost btn-sm"
                                      style={{ marginTop: 4, fontSize: 11, padding: '2px 6px' }}
                                      onClick={() => toggleSplitMode(i)}
                                    >
                                      {g.splitMode ? t('invoices.useSingle') : t('invoices.splitAcross')}
                                    </button>
                                  )}
                                  {g.splitMode && !g.skip && (
                                    <div
                                      style={{
                                        fontSize: 11,
                                        color: qtyMismatch ? 'var(--warning)' : 'var(--text-muted)',
                                        marginTop: 4,
                                      }}
                                    >
                                      {t('invoices.splitQty', { sum: splitSum, qty: g.quantity })}
                                    </div>
                                  )}
                                </td>
                                <td
                                  style={{
                                    whiteSpace: 'nowrap',
                                    color: 'var(--text-muted)',
                                    fontSize: 12,
                                  }}
                                >
                                  {g.quantity} {g.unit}
                                </td>
                                <td
                                  style={{
                                    whiteSpace: 'nowrap',
                                    color: 'var(--text-muted)',
                                    fontSize: 12,
                                  }}
                                >
                                  {g.quantity > 0 ? formatUnitPrice(g.total / g.quantity) : '—'}
                                </td>
                                <td>
                                  {g.splitMode ? (
                                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                      {splitSum}
                                    </span>
                                  ) : (
                                    <input
                                      type="number"
                                      min={0}
                                      step="any"
                                      value={g.txQuantityStr}
                                      disabled={isReadOnly}
                                      onChange={(e) => {
                                        const raw = e.target.value;
                                        const num = parseFloat(raw);
                                        updateGoods(i, {
                                          txQuantityStr: raw,
                                          txQuantity: isNaN(num) ? 0 : num,
                                        });
                                      }}
                                      onBlur={(e) => {
                                        if (
                                          e.target.value === '' ||
                                          isNaN(parseFloat(e.target.value))
                                        ) {
                                          updateGoods(i, { txQuantityStr: '0', txQuantity: 0 });
                                        }
                                      }}
                                      style={{ fontSize: 12, width: 70 }}
                                    />
                                  )}
                                </td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  {g.splitMode ? (
                                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</span>
                                  ) : g.useExisting ? (
                                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                      {linkedUnit}
                                    </span>
                                  ) : (
                                    <select
                                      value={g.newIngredientUnit}
                                      disabled={isReadOnly}
                                      onChange={(e) =>
                                        updateGoods(i, { newIngredientUnit: e.target.value })
                                      }
                                      style={{ fontSize: 12 }}
                                    >
                                      {UNITS.map((u) => (
                                        <option key={u} value={u}>
                                          {u}
                                        </option>
                                      ))}
                                    </select>
                                  )}
                                </td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  <div>
                                    {g.txQuantity > 0 ? formatUnitPrice(g.total / g.txQuantity) : '—'}
                                  </div>
                                  {!g.splitMode &&
                                    g.useExisting &&
                                    (() => {
                                      const lastPrice = ingredients.find(
                                        (ing) => ing.id === g.ingredientId,
                                      )?.cost_price;
                                      return lastPrice && lastPrice > 0 ? (
                                        <div
                                          style={{
                                            fontSize: 11,
                                            color: 'var(--text-muted)',
                                            marginTop: 2,
                                          }}
                                        >
                                          {t('invoices.lastPrice')}: {formatAMD(lastPrice)}
                                        </div>
                                      ) : null;
                                    })()}
                                </td>
                                <td style={{ whiteSpace: 'nowrap' }}>{formatAMD(g.total)}</td>
                                <td>
                                  {!g.skip && !g.splitMode ? (
                                    <div
                                      style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 6,
                                        minWidth: 180,
                                      }}
                                    >
                                      <div style={{ display: 'flex', gap: 12 }}>
                                        <label
                                          style={{
                                            display: 'flex',
                                            gap: 5,
                                            alignItems: 'center',
                                            fontSize: 12,
                                            cursor: 'pointer',
                                          }}
                                        >
                                          <input
                                            type="radio"
                                            name={`mode-${i}`}
                                            checked={g.useExisting}
                                            disabled={isReadOnly}
                                            onChange={() => updateGoods(i, { useExisting: true })}
                                          />
                                          {t('invoices.existing')}
                                        </label>
                                        <label
                                          style={{
                                            display: 'flex',
                                            gap: 5,
                                            alignItems: 'center',
                                            fontSize: 12,
                                            cursor: 'pointer',
                                          }}
                                        >
                                          <input
                                            type="radio"
                                            name={`mode-${i}`}
                                            checked={!g.useExisting}
                                            disabled={isReadOnly}
                                            onChange={() => updateGoods(i, { useExisting: false })}
                                          />
                                          {t('invoices.createNew')}
                                        </label>
                                      </div>
                                      {g.useExisting ? (
                                        <select
                                          value={g.ingredientId ?? ''}
                                          disabled={isReadOnly}
                                          onChange={(e) =>
                                            updateGoods(i, { ingredientId: e.target.value || null })
                                          }
                                          style={{
                                            fontSize: 12,
                                            borderColor:
                                              !isReadOnly && !g.ingredientId
                                                ? 'var(--danger)'
                                                : undefined,
                                          }}
                                        >
                                          <option value="">{t('invoices.selectIngredient')}</option>
                                          {ingredients.map((ing) => (
                                            <option key={ing.id} value={ing.id}>
                                              {ing.name} ({ing.unit})
                                            </option>
                                          ))}
                                        </select>
                                      ) : (
                                        <input
                                          placeholder={t('invoices.ingredientName')}
                                          value={g.newIngredientName}
                                          disabled={isReadOnly}
                                          onChange={(e) =>
                                            updateGoods(i, { newIngredientName: e.target.value })
                                          }
                                          style={{
                                            fontSize: 12,
                                            width: '100%',
                                            borderColor:
                                              !isReadOnly && !g.newIngredientName.trim()
                                                ? 'var(--danger)'
                                                : undefined,
                                          }}
                                        />
                                      )}
                                    </div>
                                  ) : g.splitMode && !g.skip ? (
                                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                      {t('invoices.ingredientCount', { count: g.splits.length })}
                                    </span>
                                  ) : null}
                                </td>
                              </tr>
                              {g.splitMode && !g.skip && (
                                <tr key={`${i}-splits`}>
                                  <td colSpan={9} style={{ background: 'var(--surface-2)', padding: '10px 14px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                      {g.splits.map((split, si) => (
                                        <div
                                          key={si}
                                          style={{
                                            display: 'grid',
                                            gridTemplateColumns: '80px 1fr auto auto',
                                            gap: 8,
                                            alignItems: 'center',
                                          }}
                                        >
                                          <input
                                            type="number"
                                            min={0}
                                            step="any"
                                            value={split.txQuantityStr}
                                            disabled={isReadOnly}
                                            placeholder={t('invoices.qtyPlaceholder')}
                                            onChange={(e) => {
                                              const raw = e.target.value;
                                              const num = parseFloat(raw);
                                              updateGoodsSplit(i, si, {
                                                txQuantityStr: raw,
                                                txQuantity: isNaN(num) ? 0 : num,
                                              });
                                            }}
                                            onBlur={(e) => {
                                              if (
                                                e.target.value === '' ||
                                                isNaN(parseFloat(e.target.value))
                                              ) {
                                                updateGoodsSplit(i, si, {
                                                  txQuantityStr: '0',
                                                  txQuantity: 0,
                                                });
                                              }
                                            }}
                                            style={{ fontSize: 12 }}
                                          />
                                          <select
                                            value={split.ingredientId ?? ''}
                                            disabled={isReadOnly || !split.useExisting}
                                            onChange={(e) =>
                                              updateGoodsSplit(i, si, {
                                                ingredientId: e.target.value || null,
                                              })
                                            }
                                            style={{
                                              fontSize: 12,
                                              borderColor:
                                                !isReadOnly &&
                                                split.useExisting &&
                                                split.txQuantity > 0 &&
                                                !split.ingredientId
                                                  ? 'var(--danger)'
                                                  : undefined,
                                            }}
                                          >
                                            <option value="">{t('invoices.splitIngredient')}</option>
                                            {ingredients.map((ing) => (
                                              <option key={ing.id} value={ing.id}>
                                                {ing.name} ({ing.unit})
                                              </option>
                                            ))}
                                          </select>
                                          <label
                                            style={{
                                              display: 'flex',
                                              gap: 4,
                                              alignItems: 'center',
                                              fontSize: 11,
                                              whiteSpace: 'nowrap',
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={!split.useExisting}
                                              disabled={isReadOnly}
                                              onChange={(e) =>
                                                updateGoodsSplit(i, si, {
                                                  useExisting: !e.target.checked,
                                                })
                                              }
                                            />
                                            {t('invoices.splitNew')}
                                          </label>
                                          {!split.useExisting ? (
                                            <input
                                              placeholder={t('invoices.newIngredientName')}
                                              value={split.newIngredientName}
                                              disabled={isReadOnly}
                                              onChange={(e) =>
                                                updateGoodsSplit(i, si, {
                                                  newIngredientName: e.target.value,
                                                })
                                              }
                                              style={{ fontSize: 12 }}
                                            />
                                          ) : (
                                            <button
                                              type="button"
                                              className="btn-ghost btn-sm"
                                              disabled={isReadOnly || g.splits.length <= 1}
                                              onClick={() => removeGoodsSplit(i, si)}
                                            >
                                              {t('invoices.removeSplit')}
                                            </button>
                                          )}
                                        </div>
                                      ))}
                                      {!isReadOnly && (
                                        <button
                                          type="button"
                                          className="btn-ghost btn-sm"
                                          style={{ alignSelf: 'flex-start' }}
                                          onClick={() => addGoodsSplit(i)}
                                        >
                                          {t('invoices.addIngredient')}
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  )}

                  {goods.length === 0 && !goodsLoading && !goodsError && (
                    <div className="empty-state" style={{ fontSize: 13 }}>
                      {t('invoices.noLineItems')}
                    </div>
                  )}
                </div>
                {/* end scrollable goods */}

                <div
                  style={{
                    marginTop: 16,
                    display: 'flex',
                    gap: 10,
                    alignItems: 'center',
                    paddingTop: 12,
                    borderTop: '1px solid var(--border)',
                  }}
                >
                  {!confirming ? (
                    <>
                      {isReadOnly ? (
                        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                          {t('invoices.alreadyApproved', {
                            status:
                              reviewing.status === 'APPROVED'
                                ? t('invoices.taxApproved').toLowerCase()
                                : t('invoices.approved').toLowerCase(),
                          })}
                        </span>
                      ) : (
                        <button
                          className="btn"
                          onClick={() => setConfirming(true)}
                          disabled={approving || goodsLoading || goods.some((g) => !isGoodsRowValid(g))}
                          title={
                            goods.some((g) => !isGoodsRowValid(g))
                              ? t('invoices.trackRequired')
                              : undefined
                          }
                        >
                          {t('invoices.approveInternal')}
                        </button>
                      )}
                      <button className="btn-ghost" onClick={() => setReviewing(null)}>
                        {isReadOnly ? t('common.close') : t('common.cancel')}
                      </button>
                    </>
                  ) : (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <span style={{ fontSize: 13, color: 'var(--text)' }}>
                          {t('invoices.confirmPrompt')}
                        </span>
                        <label
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                            fontSize: 12,
                            color: 'var(--text-muted)',
                            maxWidth: 420,
                          }}
                        >
                          {t('invoices.approvalNote')}
                          <textarea
                            className="input"
                            rows={2}
                            placeholder={t('invoices.approvalNotePlaceholder')}
                            value={approvalNote}
                            onChange={(e) => setApprovalNote(e.target.value)}
                            disabled={approving}
                            maxLength={500}
                          />
                        </label>
                      </div>
                      <button className="btn" onClick={approve} disabled={approving}>
                        {approving ? t('common.saving') : t('invoices.yesApprove')}
                      </button>
                      <button
                        className="btn-ghost"
                        onClick={() => {
                          setConfirming(false);
                          setApprovalNote('');
                        }}
                        disabled={approving}
                      >
                        {t('common.back')}
                      </button>
                    </>
                  )}
                  {approveMsg && (
                    <span
                      style={{
                        fontSize: 13,
                        color: approveMsg.ok ? 'var(--success)' : 'var(--danger)',
                      }}
                    >
                      {approveMsg.text}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
    </section>
  );
}
